import { Ease, linear, clamp01 } from '../math/easing';
import { Vec3 } from '../math/vec3';
import { SpringF } from '../math/spring';

/** A timed interpolation driving a callback; managed by a Tweens list. */
export class Tween {
  t = 0;
  done = false;
  constructor(
    readonly duration: number,
    readonly onUpdate: (k: number, raw: number) => void,
    readonly ease: Ease = linear,
    readonly onComplete?: () => void,
    readonly delay = 0,
  ) {}
  update(dt: number): void {
    if (this.done) return;
    this.t += dt;
    const raw = clamp01((this.t - this.delay) / this.duration);
    if (this.t < this.delay) return;
    this.onUpdate(this.ease(raw), raw);
    if (raw >= 1) { this.done = true; this.onComplete?.(); }
  }
}

export class Tweens {
  private list: Tween[] = [];
  add(duration: number, onUpdate: (k: number, raw: number) => void, ease?: Ease, onComplete?: () => void, delay?: number): Tween {
    const tw = new Tween(duration, onUpdate, ease, onComplete, delay);
    this.list.push(tw);
    return tw;
  }
  /** Fire a callback after a delay. */
  after(delay: number, fn: () => void): Tween { return this.add(0.0001, () => {}, linear, fn, delay); }
  /** Sequence of [duration, fn] steps; each fn receives eased k. */
  update(dt: number): void {
    for (const t of this.list) t.update(dt);
    if (this.list.some((t) => t.done)) this.list = this.list.filter((t) => !t.done);
  }
  cancel(t: Tween): void { t.done = true; }
  clear(): void { this.list.length = 0; }
  get count(): number { return this.list.length; }
}

/**
 * Volume-preserving squash & stretch driven by springs. Set `stretch` target along an axis
 * (1 = neutral) and read `scale` each frame. Kicks return to 1 with overshoot.
 */
export class SquashStretch {
  readonly scale = new Vec3(1, 1, 1);
  /** Along-axis stretch factor; perpendicular axes get 1/sqrt(s). */
  readonly s: SpringF;
  readonly axis = new Vec3(0, 1, 0);
  constructor(freqHz = 6, zeta = 0.35) {
    this.s = SpringF.fromFreq(1, freqHz, zeta);
  }
  /** Instant deformation that springs back. e.g. land: squash(0.7) ; jump: squash(1.3). */
  squash(amount: number, axis?: Vec3): void {
    if (axis) this.axis.copy(axis).normalize();
    this.s.value = amount;
    this.s.velocity = 0;
  }
  /** Velocity kick (feels punchier than an instant set). */
  kick(v: number): void { this.s.kick(v); }
  update(dt: number): Vec3 {
    const s = this.s.update(dt);
    const p = 1 / Math.sqrt(Math.max(0.05, s));
    // blend between axis-aligned Y stretch and general: we only ever stretch along Y or a horizontal axis
    const ax = this.axis;
    const ay = Math.abs(ax.y);
    if (ay > 0.9) this.scale.set(p, s, p);
    else {
      // horizontal stretch along a direction: approximate with XZ-uniform stretch, Y squash
      const h = Math.sqrt(s);
      this.scale.set(h, p, h);
    }
    return this.scale;
  }
  reset(): void { this.s.snap(1); this.scale.set(1, 1, 1); }
}
